import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect, createContext, useContext, useRef } from "react";
import {
  ArrowRight,
  Menu,
  X,
  MessageCircle,
  ShieldCheck,
  Timer,
  Wallet,
  ChevronDown,
  Check,
  Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealStagger } from "@/components/motion/Reveal";
import { PricingPremium } from "@/components/PricingPremium";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

// ─── Currency (local to this page) ───
type Currency = "CHF" | "EUR";
const EUR_PER_CHF = 1 / 0.92;
const CurrencyCtx = createContext<{ currency: Currency; setCurrency: (c: Currency) => void }>({
  currency: "CHF",
  setCurrency: () => {},
});
function useFormat() {
  const { currency } = useContext(CurrencyCtx);
  return (chf: number, opts: { withCode?: boolean; suffix?: string } = {}) => {
    const { withCode = true, suffix = "" } = opts;
    const value = currency === "CHF" ? chf : Math.round(chf * EUR_PER_CHF);
    const s = value.toString();
    const formatted = s.length <= 3 ? s : s.slice(0, -3) + (currency === "CHF" ? "'" : ".") + s.slice(-3);
    return `${withCode ? currency + " " : ""}${formatted}${suffix}`;
  };
}

const WA_URL =
  "https://wa.me/41782369200?text=" +
  encodeURIComponent(
    "Ciao Filippo,\n\nHo visto la pagina Prezzi e vorrei un'informazione sul piano più adatto.\n\nNome attività:\nSettore:\nTelefono:\n\nAttendo il tuo contatto."
  );
const FLEX_WA_URL =
  "https://wa.me/41782369200?text=" +
  encodeURIComponent(
    "Ciao Filippo,\n\nVorrei attivare il piano FlashWeb Flex (CHF 59/mese, 0 CHF di attivazione).\n\nNome attività:\nSettore:\nTelefono:\n\nAttendo il vostro contatto."
  );

export const Route = createFileRoute("/prezzi")({
  head: () => ({
    meta: [
      { title: "Prezzi — Da CHF 59/mese o CHF 990 una tantum | FlashWeb" },
      {
        name: "description",
        content:
          "Confronta FlashWeb Flex, Sito Professionale e Sito Completo. Configuratore interattivo per scoprire qual è il piano giusto per te.",
      },
      { property: "og:title", content: "Prezzi — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "Da CHF 59/mese o CHF 990 una tantum. Configuratore interattivo e tabella comparativa completa.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/prezzi" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/prezzi" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Prezzi", item: "https://www.flash-web.ch/prezzi" },
          ],
        }),
      },
    ],
  }),
  component: PrezziPage,
});

// ─── Navbar ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

// ─── Footer ───
function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════ CONFIGURATOR ═══════════════

type Answer = "1" | "2-4" | "5+" | "yes" | "no" | "have" | "need" | null;
type Recommendation = "flex" | "landing" | "premium";

function recommend(pages: Answer, maintenance: Answer, content: Answer): Recommendation {
  // Manutenzione continua → Flex vince
  if (maintenance === "yes") return "flex";
  // Molte pagine → Premium
  if (pages === "5+") return "premium";
  // Ha bisogno di aiuto contenuti + una tantum → Premium
  if (content === "need" && pages === "2-4") return "premium";
  // Default una-tantum con 1 pagina
  return "landing";
}

const REC_META: Record<Recommendation, { name: string; anchor: string; blurb: string }> = {
  flex: {
    name: "FlashWeb Flex",
    anchor: "plan-flex",
    blurb: "Abbonamento tutto incluso: manutenzione, aggiornamenti e modifiche continue.",
  },
  landing: {
    name: "Sito Professionale",
    anchor: "plan-landing",
    blurb: "Una tantum, online in 48 ore. Pagata una volta, tua per sempre.",
  },
  premium: {
    name: "Sito Completo",
    anchor: "plan-premium",
    blurb: "Fino a 5 pagine, design premium, animazioni e integrazioni.",
  },
};

function Configurator({ onRecommend }: { onRecommend: (r: Recommendation) => void }) {
  const [pages, setPages] = useState<Answer>(null);
  const [maintenance, setMaintenance] = useState<Answer>(null);
  const [content, setContent] = useState<Answer>(null);

  const complete = pages && maintenance && content;
  const rec = useMemo(() => (complete ? recommend(pages, maintenance, content) : null), [complete, pages, maintenance, content]);

  return (
    <div className="mx-auto max-w-3xl rounded-3xl border border-border bg-card/40 p-6 backdrop-blur sm:p-10">
      <Question
        n={1}
        title="Quante pagine ti servono?"
        options={[
          { v: "1", label: "1 pagina" },
          { v: "2-4", label: "2–4 pagine" },
          { v: "5+", label: "5 o più" },
        ]}
        value={pages}
        onChange={setPages}
      />
      <Question
        n={2}
        title="Ti serve anche gestione mensile / modifiche continue?"
        options={[
          { v: "yes", label: "Sì, vorrei non pensarci" },
          { v: "no", label: "No, una tantum va bene" },
        ]}
        value={maintenance}
        onChange={setMaintenance}
      />
      <Question
        n={3}
        title="Hai già foto e testi pronti?"
        options={[
          { v: "have", label: "Sì, ce li ho" },
          { v: "need", label: "No, mi serve aiuto" },
        ]}
        value={content}
        onChange={setContent}
      />

      {rec && (
        <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-primary/40 bg-primary/5 p-6 animate-fade-in sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Consigliato per te</p>
            <h3 className="mt-1 font-display text-xl font-bold sm:text-2xl">{REC_META[rec].name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{REC_META[rec].blurb}</p>
          </div>
          <Button variant="hero" size="lg" onClick={() => onRecommend(rec)} className="shrink-0 fw-shimmer">
            Vedi il piano <ArrowRight />
          </Button>
        </div>
      )}
    </div>
  );
}

function Question({
  n,
  title,
  options,
  value,
  onChange,
}: {
  n: number;
  title: string;
  options: { v: Exclude<Answer, null>; label: string }[];
  value: Answer;
  onChange: (a: Answer) => void;
}) {
  return (
    <div className="mb-6 last:mb-0">
      <p className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">
          {n}
        </span>
        {title}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                active
                  ? "border-primary bg-primary text-primary-foreground shadow-[0_0_20px_color-mix(in_oklab,var(--primary)_40%,transparent)]"
                  : "border-border bg-background/40 text-foreground/80 hover:border-primary/50 hover:bg-primary/5"
              }`}
              aria-pressed={active}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════ COMPARISON TABLE ═══════════════

type Cell = string | boolean;
type PlanKey = "flex" | "pro" | "completo";
const PLANS: { key: PlanKey; name: string; sub: string; featured?: boolean }[] = [
  { key: "flex", name: "FlashWeb Flex", sub: "Abbonamento" },
  { key: "pro", name: "Sito Professionale", sub: "Una tantum" },
  { key: "completo", name: "Sito Completo", sub: "Una tantum", featured: true },
];
const ROWS: { label: string; values: Record<PlanKey, Cell> }[] = [
  { label: "Numero pagine", values: { flex: "1 pagina", pro: "1 pagina", completo: "Fino a 5 pagine" } },
  { label: "Tempo di consegna", values: { flex: "48 ore", pro: "48 ore", completo: "72 ore" } },
  { label: "Design su misura", values: { flex: true, pro: true, completo: "Premium" } },
  { label: "SEO on-page", values: { flex: true, pro: true, completo: "Avanzata" } },
  { label: "Animazioni & integrazioni", values: { flex: false, pro: false, completo: true } },
  { label: "Dashboard cliente", values: { flex: false, pro: false, completo: true } },
  { label: "Hosting & dominio", values: { flex: "Inclusi", pro: "1° anno incluso", completo: "1° anno incluso" } },
  { label: "Aggiornamenti mensili", values: { flex: "Illimitati", pro: "A pagamento", completo: "1 mese incluso" } },
  { label: "Revisioni incluse", values: { flex: "Illimitate", pro: "1", completo: "2" } },
  { label: "Prezzo", values: { flex: "CHF 59/mese", pro: "CHF 990", completo: "CHF 1'790" } },
];

function renderCell(v: Cell) {
  if (v === true) return <Check className="mx-auto h-5 w-5 text-primary" aria-label="Incluso" />;
  if (v === false) return <Minus className="mx-auto h-5 w-5 text-muted-foreground/50" aria-label="Non incluso" />;
  return <span className="text-sm">{v}</span>;
}

function ComparisonTable() {
  return (
    <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-border bg-card/40 backdrop-blur">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left">
          <thead className="bg-background/60">
            <tr className="border-b border-border">
              <th className="px-4 py-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">Feature</th>
              {PLANS.map((p) => (
                <th
                  key={p.key}
                  className={`px-4 py-4 text-center ${p.featured ? "bg-primary/5" : ""}`}
                >
                  <div className={`font-display text-sm font-bold ${p.featured ? "text-primary" : "text-foreground"}`}>
                    {p.name}
                  </div>
                  <div className="text-[11px] font-medium text-muted-foreground">{p.sub}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.label} className="border-b border-border/50 last:border-0">
                <td className="px-4 py-3 text-sm font-medium text-foreground/90">{r.label}</td>
                {PLANS.map((p) => (
                  <td
                    key={p.key}
                    className={`px-4 py-3 text-center ${p.featured ? "bg-primary/5" : ""}`}
                  >
                    {renderCell(r.values[p.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════ FAQ ═══════════════

const FAQS = [
  {
    q: "Come si paga? È previsto un pagamento rateale?",
    a: "Sito Professionale e Sito Completo: 50% all'ordine e 50% alla pubblicazione. FlashWeb Flex: addebito mensile, zero attivazione. Su richiesta valutiamo rateizzazioni personalizzate per progetti sopra CHF 2'000.",
  },
  {
    q: "Cosa succede se disdico FlashWeb Flex?",
    a: "Puoi disdire in qualsiasi momento senza penali. Il sito resta online fino alla fine del mese pagato; poi puoi trasferirlo su un altro hosting o convertirlo in una licenza una-tantum.",
  },
  {
    q: "I prezzi sono IVA inclusa?",
    a: "I prezzi mostrati sono al netto di IVA. Per clienti svizzeri applichiamo IVA CH (8.1%). Per clienti UE B2B con partita IVA valida applichiamo reverse charge (nessuna IVA in fattura).",
  },
  {
    q: "Il prezzo può cambiare in corso d'opera?",
    a: "No. Il prezzo comunicato prima dell'ordine è quello finale. Eventuali richieste extra (pagine aggiuntive, integrazioni fuori scope) sono sempre preventivate e approvate separatamente prima di procedere.",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card/40 backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-display text-base font-semibold">{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-border/60 px-5 py-4 text-sm text-muted-foreground">{a}</div>}
    </div>
  );
}

// ═══════════════ PAGE ═══════════════

function PrezziPage() {
  const [currency, setCurrency] = useState<Currency>("CHF");
  const [highlight, setHighlight] = useState<Recommendation | null>(null);
  const pricingRef = useRef<HTMLDivElement>(null);

  const handleRecommend = (r: Recommendation) => {
    setHighlight(r);
    // scroll to the specific card
    requestAnimationFrame(() => {
      const el = document.getElementById(REC_META[r].anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const openHome = (pkg: "landing" | "business") => {
    window.location.href = `/?pkg=${pkg}#price`;
  };

  return (
    <CurrencyCtx.Provider value={{ currency, setCurrency }}>
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />

        {/* 1. HERO */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
          <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
          <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Prezzi
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
                Un prezzo. <span className="text-gradient inline-block">Nessuna sorpresa.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
                Scegli in base a come vuoi lavorare con noi, non in base a feature che non capisci.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex items-center justify-center gap-3">
                <div className="flex items-center gap-0.5 rounded-full border border-border/60 bg-background p-0.5">
                  {(["CHF", "EUR"] as const).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCurrency(c)}
                      aria-pressed={currency === c}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                        currency === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* 2. PRICING CARDS (reused) */}
        <div ref={pricingRef}>
          <PricingCardsWrapper
            highlight={highlight}
            onOrderLanding={() => openHome("landing")}
            onOrderPremium={() => openHome("business")}
            flexWaUrl={FLEX_WA_URL}
          />
        </div>

        {/* 3. CONFIGURATORE */}
        <section className="relative border-t border-border/60 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-6">
            <Reveal>
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Configuratore</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  Non sai quale scegliere?
                </h2>
                <p className="mt-4 text-muted-foreground">
                  Rispondi a 3 domande. Ti indichiamo il piano giusto in 30 secondi.
                </p>
              </div>
            </Reveal>
            <div className="mt-12">
              <Configurator onRecommend={handleRecommend} />
            </div>
          </div>
        </section>

        {/* 4. TABELLA COMPARATIVA */}
        <section className="relative border-t border-border/60 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-6">
            <Reveal>
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Confronto</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  Cosa include ogni piano.
                </h2>
                <p className="mt-4 text-muted-foreground">
                  Tutto, riga per riga. Nessun asterisco.
                </p>
              </div>
            </Reveal>
            <div className="mt-12">
              <ComparisonTable />
            </div>
            <div className="mt-6 text-center text-sm">
              <Link to="/servizi" className="font-semibold text-primary hover:underline">
                Vedi la pagina Servizi con tutti i dettagli →
              </Link>
            </div>
          </div>
        </section>

        {/* 5. GARANZIE */}
        <section className="relative border-t border-border/60 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-6">
            <Reveal>
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Garanzie</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  Cosa promettiamo, per iscritto.
                </h2>
              </div>
            </Reveal>
            <div className="mt-12 grid gap-4 sm:grid-cols-3">
              <RevealStagger>
                <GuaranteeCard
                  icon={<Wallet className="h-5 w-5 text-primary" />}
                  title="Tariffa fissa"
                  body="Il prezzo che leggi è quello che paghi. Zero costi extra a metà progetto."
                />
                <GuaranteeCard
                  icon={<Timer className="h-5 w-5 text-primary" />}
                  title="Consegna nei tempi"
                  body="48h per Professionale, 72h per Completo. Attivazione stessa settimana per Flex."
                />
                <GuaranteeCard
                  icon={<ShieldCheck className="h-5 w-5 text-primary" />}
                  title="In caso di ritardo"
                  body="Se sforiamo i tempi per causa nostra, applichiamo uno sconto proporzionale. Dettagli in FAQ."
                />
              </RevealStagger>
            </div>
          </div>
        </section>

        {/* 6. FAQ */}
        <section className="relative border-t border-border/60 py-20 sm:py-28">
          <div className="mx-auto max-w-3xl px-6">
            <Reveal>
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Domande sui prezzi</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  Le risposte prima di ordinare.
                </h2>
              </div>
            </Reveal>
            <div className="mt-10 space-y-3">
              {FAQS.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} />
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/faq" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                Vedi tutte le FAQ <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* 7. CTA FINALE */}
        <section className="relative overflow-hidden py-20 sm:py-28">
          <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
          <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
          <div className="relative mx-auto max-w-3xl px-6 text-center">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                Pronto a partire? <span className="text-gradient inline-block">48 ore da adesso.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="mx-auto mt-4 max-w-xl text-muted-foreground sm:text-lg">
                Configura il tuo sito, oppure scrivici su WhatsApp: rispondiamo in giornata.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button variant="hero" size="xl" asChild className="fw-shimmer">
                  <Link to="/">
                    Configura il tuo sito <ArrowRight />
                  </Link>
                </Button>
                <Button variant="outlineHero" size="xl" asChild>
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                    <MessageCircle /> Scrivici su WhatsApp
                  </a>
                </Button>
              </div>
            </Reveal>
          </div>
        </section>

        <Footer />
      </div>
    </CurrencyCtx.Provider>
  );
}

// Wraps PricingPremium so it re-renders when currency changes via hook.
function PricingCardsWrapper({
  highlight,
  onOrderLanding,
  onOrderPremium,
  flexWaUrl,
}: {
  highlight: Recommendation | null;
  onOrderLanding: () => void;
  onOrderPremium: () => void;
  flexWaUrl: string;
}) {
  const format = useFormat();
  return (
    <PricingPremium
      format={format}
      onOrderLanding={onOrderLanding}
      onOrderPremium={onOrderPremium}
      flexWaUrl={flexWaUrl}
      enterpriseUrl="mailto:info@flash-web.ch"
      highlightPlan={highlight}
    />
  );
}

function GuaranteeCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-4 rounded-2xl border border-border bg-card/40 p-6 backdrop-blur">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-background">
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="font-display text-lg font-bold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

// Suppress unused-import warning for useEffect (kept for future extension)
void useEffect;
